import {Box, Container, Typography} from '@mui/material';
import {Accordion, Button} from '@ghs/components';
import {useUnit} from 'effector-react';
import useMountedEvent from '../../hooks/useMountedEvent';
import {
    hasKnowledgeBasePermission,
    hasReleaseNotesPermission
} from '../../services/PermissionService/PermissionService';
import {$$gcp} from '../../services/GcpService/model';
import {$$documentation} from './model';
import {$$app} from '../../components/App/model';
import ReleaseNotesDialog from './ReleaseNotesDialog';
import {useTheme} from '@mui/material/styles';

export default function DocumentationPage() {
  useMountedEvent($$documentation.pageMounted);

  const userDetails = useUnit($$app.$userDetails);
  const faqs = useUnit($$gcp.$faqs);

  const docHtml = useUnit($$documentation.$documentationHtml);
  const theme = useTheme();

  const handleClick = e => {
    // Open the image in a new tab
    if (e.target.tagName === 'IMG') {
      window.open(e.target.src, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <>
      <ReleaseNotesDialog />

      <Container id="documentation-container">
        {hasReleaseNotesPermission(userDetails?.permissions) && (
          <Button color="primary" onClick={() => $$documentation.releaseNotesButtonClicked()} size="medium" variant="contained" sx={{ mb: 0 }}>
            Release Notes
          </Button>
        )}

        {hasKnowledgeBasePermission(userDetails?.permissions) ? (
          <Box
            id="knowledge-base-container"
            sx={{
              ...theme.typography.body1,
              color: theme.palette.text.primary,
              '& img': {
                width: 'auto',
                height: 'auto',
                maxWidth: '400px',
                // Link pointer
                '&:hover': {
                  cursor: 'pointer'
                }
              },
              // Override default style to visited links and keep them all blue
              '& a, & a:visited': {
                color: 'blue'
              },
              // Fix error with fixed headers overlapping anchor targets.
              // The application bar is defined with height 50px so we'll use 70px to take margin into account
              '& h1[id], & h2[id], & h3[id], & h4[id], & h5[id], & h6[id], & div#document-top': {
                scrollMarginTop: '70px'
              },
              // Indent elements in TOC
              '& .index_level_1': {
                marginLeft: '18pt'
              },
              '& .index_level_2': {
                marginLeft: '36pt'
              },
              // Custom styles for Confluence documentation tables
              '& .confluenceTable': {
                borderCollapse: 'collapse',
                width: '100%'
              },
              '& .confluenceTh': {
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                border: `1px solid ${theme.palette.divider}`,
                padding: theme.spacing(1),
                textAlign: 'left'
              },
              '& .confluenceTd': {
                border: `1px solid ${theme.palette.divider}`,
                padding: theme.spacing(1)
              }
            }}
            onClick={handleClick}
            dangerouslySetInnerHTML={{ __html: docHtml }}
          />
        ) : (
          <Container id="faq-container">
            <Typography variant="h4" color="text.primary" sx={{ mt: 2, mb: 6 }}>
              Frequently Asked Questions
            </Typography>

            {faqs.map((faq, index) => {
              return (
                <Accordion key={`faq-${index}`} heading={faq.question}>
                  <Container>
                    <Typography variant="string">
                      <span>{faq.answer}</span>
                    </Typography>
                  </Container>
                </Accordion>
              );
            })}
          </Container>
        )}
      </Container>
    </>
  );
}
